import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://static.readdy.ai/image/461a3833cd00dd501e90f2080ae5acc6/aae23f0a089f1ffcf8ba37bc7395e262.png';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [divisionsOpen, setDivisionsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDivisionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDivisionsOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;
  const isDivisionActive = location.pathname.startsWith('/telecommunications');

  const linkClass = (path: string) =>
    `text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
      scrolled || mobileOpen
        ? isActive(path)
          ? 'text-[#0050B2]'
          : 'text-[#001F4E] hover:text-[#0050B2]'
        : isActive(path)
        ? 'text-white font-semibold'
        : 'text-white/90 hover:text-white'
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer shrink-0 max-w-[160px] sm:max-w-[200px] lg:max-w-none">
            <img
              src={LOGO_URL}
              alt="Holston Development Group"
              className={`h-7 sm:h-9 lg:h-12 w-auto max-w-full object-contain transition-all duration-300 ${!scrolled && !mobileOpen ? 'brightness-0 invert drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]' : ''}`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className={linkClass('/')}>Home</Link>
            <Link to="/about" className={linkClass('/about')}>About</Link>

            {/* Divisions Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDivisionsOpen(!divisionsOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? isDivisionActive ? 'text-[#0050B2]' : 'text-[#001F4E] hover:text-[#0050B2]'
                    : isDivisionActive ? 'text-white font-semibold' : 'text-white/90 hover:text-white'
                }`}
              >
                Divisions
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${divisionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {divisionsOpen && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-100 py-1 overflow-hidden">
                  <Link
                    to="/telecommunications"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-[#001F4E] hover:bg-[#E8EFF6] hover:text-[#0050B2] transition-colors cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-signal-tower-line text-[#0050B2]" />
                    </div>
                    Telecommunications
                  </Link>
                  <a
                    href="https://holstonlandmgmt.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-[#001F4E] hover:bg-[#E8EFF6] hover:text-[#0050B2] transition-colors cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-landscape-line text-[#0050B2]" />
                    </div>
                    Land Services
                    <i className="ri-external-link-line text-xs text-gray-400" />
                  </a>
                </div>
              )}
            </div>

            <Link to="/contact" className={linkClass('/contact')}>Contact</Link>

            <Link
              to="/contact"
              className="ml-2 px-5 py-2 rounded-md bg-[#0050B2] text-white text-sm font-medium hover:bg-[#001F4E] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Get in Touch
            </Link>
          </nav>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
              scrolled || mobileOpen ? 'text-[#001F4E]' : 'text-white'
            }`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-gray-100 mt-1 pt-3 space-y-1">
            <Link to="/" className="block px-2 py-2.5 text-sm font-medium text-[#001F4E] hover:text-[#0050B2] cursor-pointer">Home</Link>
            <Link to="/about" className="block px-2 py-2.5 text-sm font-medium text-[#001F4E] hover:text-[#0050B2] cursor-pointer">About</Link>
            <button
              onClick={() => setMobileAccordion(!mobileAccordion)}
              className="flex items-center justify-between w-full px-2 py-2.5 text-sm font-medium text-[#001F4E] hover:text-[#0050B2] cursor-pointer"
            >
              Divisions
              <i className={`ri-arrow-down-s-line transition-transform ${mobileAccordion ? 'rotate-180' : ''}`} />
            </button>
            {mobileAccordion && (
              <>
                <Link
                  to="/telecommunications"
                  className="block px-6 py-2 text-sm text-[#0050B2] cursor-pointer hover:underline"
                >
                  Telecommunications
                </Link>
                <a
                  href="https://holstonlandmgmt.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-6 py-2 text-sm text-[#0050B2] cursor-pointer hover:underline flex items-center gap-2"
                >
                  Land Services
                  <i className="ri-external-link-line text-xs text-gray-400" />
                </a>
              </>
            )}
            <Link to="/contact" className="block px-2 py-2.5 text-sm font-medium text-[#001F4E] hover:text-[#0050B2] cursor-pointer">Contact</Link>
            <div className="pt-2">
              <Link
                to="/contact"
                className="block text-center px-4 py-2.5 rounded-md bg-[#0050B2] text-white text-sm font-medium hover:bg-[#001F4E] transition-colors cursor-pointer whitespace-nowrap"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
